/**
 * SoundService — минималистичный синглтон для звуков.
 * Звуки генерируются через Web Audio API (не нужно подгружать файлы).
 * Когда будут готовы аудиофайлы — заменить generate* на AudioBuffer из fetch.
 */

export type SoundName = "dice" | "checker" | "win" | "lose" | "click" | "coin";

class SoundService {
  private static _instance: SoundService | null = null;
  private ctx: AudioContext | null = null;
  private enabled = true;
  private volume  = 0.6;

  static get instance(): SoundService {
    if (!this._instance) this._instance = new SoundService();
    return this._instance;
  }

  /** Разблокировать AudioContext при первом пользовательском жесте.
   *  Вызывается из App.tsx на первый touch/click на странице. */
  unlock() {
    try {
      const ctx = this.getCtx();
      if (ctx.state === "suspended") ctx.resume();
    } catch (_) {}
  }

  private getCtx(): AudioContext {
    if (!this.ctx) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume();
    }
    return this.ctx;
  }

  setEnabled(v: boolean)  { this.enabled = v; }
  setVolume(v: number)    { this.volume = Math.max(0, Math.min(1, v)); }

  play(name: SoundName) {
    if (!this.enabled) return;
    try {
      switch (name) {
        case "dice":    this.playDice();    break;
        case "checker": this.playChecker(); break;
        case "win":     this.playWin();     break;
        case "lose":    this.playLose();    break;
        case "click":   this.playClick();   break;
        case "coin":    this.playCoin();    break;
      }
    } catch (_) {}
  }

  private tone(
    freq: number, duration: number, type: OscillatorType = "sine",
    gainStart = 0.3, gainEnd = 0
  ) {
    const ctx  = this.getCtx();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    const vol  = ctx.createGain();
    vol.gain.value = this.volume;
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(gainStart, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      Math.max(gainEnd, 0.001), ctx.currentTime + duration
    );
    osc.connect(gain);
    gain.connect(vol);
    vol.connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
  }

  private noise(duration: number, gainVal = 0.15) {
    const ctx    = this.getCtx();
    const buf    = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src  = ctx.createBufferSource();
    const gain = ctx.createGain();
    const vol  = ctx.createGain();
    src.buffer = buf;
    gain.gain.setValueAtTime(gainVal * this.volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    src.connect(gain);
    gain.connect(vol);
    vol.connect(ctx.destination);
    src.start(ctx.currentTime);
  }

  private playDice() {
    // Кости катятся по доске — серия быстрых ударов, затем финальный стук
    const hits = [0, 55, 110, 165, 210, 260, 310, 360];
    hits.forEach((t, i) => {
      const vol = i < hits.length - 1 ? 0.18 + Math.random() * 0.1 : 0.35;
      setTimeout(() => {
        this.noise(0.04, vol);
        if (i % 2 === 0) this.tone(160 + Math.random() * 60, 0.05, "sine", 0.06, 0);
      }, t);
    });
    // Финальный стук о поверхность
    setTimeout(() => {
      this.noise(0.06, 0.4);
      this.tone(140, 0.12, "sine", 0.12, 0);
    }, 420);
  }

  private playChecker() {
    // Шашка опускается на доску — деревянный щелчок
    this.noise(0.035, 0.35);
    this.tone(320, 0.06, "sine", 0.18, 0);
    setTimeout(() => this.tone(220, 0.04, "sine", 0.08, 0), 30);
  }

  private playWin() {
    const notes = [523, 659, 784, 1046];
    notes.forEach((f, i) => setTimeout(() => this.tone(f, 0.3, "sine", 0.2, 0), i * 120));
  }

  private playLose() {
    const notes = [400, 350, 300, 250];
    notes.forEach((f, i) => setTimeout(() => this.tone(f, 0.3, "sawtooth", 0.1, 0), i * 100));
  }

  private playClick() {
    this.tone(800, 0.05, "square", 0.1, 0);
  }

  private playCoin() {
    this.tone(1200, 0.12, "sine", 0.15, 0);
    setTimeout(() => this.tone(1600, 0.12, "sine", 0.1, 0), 80);
  }
}

export default SoundService;
